import { Integration } from '@/middleware/integration';
import {
    AUTH_PROVIDER_KEY,
    LAST_LOGIN_KEY,
    SVVTOKENS_KEY,
    parseJwt,
} from '@/utils/AuthenticationUtils';
import { handleOAuth2Callback, hasOAuthCallback } from '@/utils/pkce';
import { getRawConfigValue } from '@/utils/runtimeEnvironment';
import { ActionType, GetState, UserLoginAction, UserLogoutAction } from './actiontypes';
import { updateSelectedFeature } from './valgtActions';
import { Dispatch } from '@/state/store';

export const userLogin =
    (tokens: { id_token: string; access_token: string }) =>
    (dispatch: Dispatch, _: GetState, integration: Integration) => {
        const jsonTokens = JSON.stringify(tokens);
        sessionStorage.setItem(SVVTOKENS_KEY, jsonTokens);
        sessionStorage.setItem(LAST_LOGIN_KEY, new Date().toString());
        // Clean up localStorage (used by popup for cross-window transfer)
        localStorage.removeItem(SVVTOKENS_KEY);
        localStorage.removeItem(LAST_LOGIN_KEY);
        integration.server.setSVVToken(tokens.access_token);
        const userLoginAction: UserLoginAction = {
            type: ActionType.LOGIN,
            idToken: tokens.id_token,
        };
        dispatch(userLoginAction);
        dispatch(updateSelectedFeature());
    };

function getEksternLogoutUrl(): string {
    const goto = encodeURIComponent(window.location.href);
    const base = getRawConfigValue('ekstern_logout_url');
    return `${base}&goto=${goto}`;
}

export const userLogout =
    () => (dispatch: Dispatch, _: GetState, integration: Integration) => {
        const tokensRaw = sessionStorage.getItem(SVVTOKENS_KEY);
        const tokens = tokensRaw ? JSON.parse(tokensRaw) : null;
        const idToken: string | undefined = tokens?.id_token;
        const provider = sessionStorage.getItem(AUTH_PROVIDER_KEY);

        sessionStorage.removeItem(SVVTOKENS_KEY);
        sessionStorage.removeItem(LAST_LOGIN_KEY);
        sessionStorage.removeItem(AUTH_PROVIDER_KEY);
        integration.server.setSVVToken();
        const userLogoutAction: UserLogoutAction = {
            type: ActionType.LOGOUT,
        };
        dispatch(userLogoutAction);
        dispatch(updateSelectedFeature());

        if (provider === 'idporten') {
            window.location.href = getEksternLogoutUrl();
        } else if (idToken) {
            try {
                const { iss } = parseJwt(idToken);
                if (iss) {
                    const postLogoutUri =
                        window.location.origin + '/oauth-callback.html?logout=true';
                    const endSessionUrl = new URL(iss + '/connect/endSession');
                    endSessionUrl.searchParams.set('id_token_hint', idToken);
                    endSessionUrl.searchParams.set(
                        'post_logout_redirect_uri',
                        postLogoutUri
                    );
                    localStorage.setItem('oauth_return_hash', window.location.hash);
                    window.location.href = endSessionUrl.toString();
                }
            } catch {
                // id_token not parseable — local logout already done above
            }
        }
    };

export const processOAuthCallback = () => async (dispatch: Dispatch) => {
    if (!hasOAuthCallback()) {
        return;
    }

    const tokens = await handleOAuth2Callback();
    if (tokens?.access_token && tokens.id_token) {
        dispatch(
            userLogin({
                id_token: tokens.id_token,
                access_token: tokens.access_token,
            })
        );
    }
};
